import "./EditExpenseReportForm.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExpenseReportItem } from "../models/expense_report_item";
import { apiCall } from "../api/apiCall";
import { useTranslation } from "../../src/i18n/UseTranslation";

type EditExpenseReportItemFormProps = {
  item: ExpenseReportItem;
};

function EditExpenseReportItemForm({ item }: EditExpenseReportItemFormProps) {
  const navigate = useNavigate();
  const t = useTranslation();
  // const translateCat = t.t.categoryTypes;
  const translations = t.t.editExpenseReportItem;

  const [label, setLabel] = useState<string>(item.label ?? "");

  const [itemType, setItemType] = useState<string>(
    item.item_type || "STANDARD",
  );

  const [quantity, setQuantity] = useState<string>(
    item.quantity != null ? String(item.quantity) : "1",
  );

  const [unitAmountTtc, setUnitAmountTtc] = useState<string>(
    item.unit_amount_ttc != null ? String(item.unit_amount_ttc) : "",
  );

  const [amountTtc, setAmountTtc] = useState<string>(
    item.amount_ttc != null ? String(item.amount_ttc) : "0",
  );

  const [vatRate, setVatRate] = useState<string>(
    item.vat_rate != null ? String(item.vat_rate) : "",
  );

  const [vatAmount, setVatAmount] = useState<string>(
    item.vat_amount != null ? String(item.vat_amount) : "",
  );

  const [amountHt, setAmountHt] = useState<string>(
    item.amount_ht != null ? String(item.amount_ht) : "",
  );

  const [isVatRecoverable, setIsVatRecoverable] = useState<boolean>(
    Boolean(item.is_vat_recoverable),
  );

  const [comment, setComment] = useState<string>(item.comment ?? "");

  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const payload = {
        label: label.trim(),
        item_type: itemType,
        quantity: Number(quantity),

        unit_amount_ttc: unitAmountTtc === "" ? null : Number(unitAmountTtc),

        amount_ttc: Number(amountTtc),

        vat_rate: vatRate === "" ? null : Number(vatRate),

        vat_amount: vatAmount === "" ? 0 : Number(vatAmount),

        amount_ht: amountHt === "" ? null : Number(amountHt),

        is_vat_recoverable: isVatRecoverable,
        comment: comment.trim() || null,
      };

      await apiCall(`/items/${item.uuid}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      alert(translations.success);
      navigate(-1);
    } catch (error) {
      console.error("Error updating expense item:", error);

      alert(translations.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-wrapper edit-expense-page">
      <form className="edit-expense-form-card" onSubmit={handleSubmit}>
        <div className="edit-expense-form-header">
          <div>
            <h1>{translations.title}</h1>
            <p>{translations.subtitle}</p>
          </div>
        </div>

        <div className="edit-expense-form-grid">
          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-label">
              {translations.fields.label}
            </label>

            <input
              id="expense-item-label"
              type="text"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              required
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-type">
              {translations.fields.itemType}
            </label>

            <select
              id="expense-item-type"
              value={itemType}
              onChange={(event) => setItemType(event.target.value)}
            >
              <option value="STANDARD">
                {translations.itemTypes.standard}
              </option>

              <option value="MEAL">{translations.itemTypes.meal}</option>

              <option value="FUEL">{translations.itemTypes.fuel}</option>

              <option value="HOUSING">{translations.itemTypes.housing}</option>

              <option value="TRAVEL">{translations.itemTypes.travel}</option>

              <option value="OFFICE">{translations.itemTypes.office}</option>
              <option value="TRAINING">
                {translations.itemTypes.training}
              </option>

              <option value="VEHICLE">{translations.itemTypes.vehicle}</option>

              <option value="EQUIPMENT">
                {translations.itemTypes.equipment}
              </option>

              <option value="PHONE">{translations.itemTypes.phone}</option>

              <option value="PRESSING">
                {translations.itemTypes.pressing}
              </option>

              <option value="OTHER">{translations.itemTypes.other}</option>
            </select>
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-quantity">
              {translations.fields.quantity}
            </label>

            <input
              id="expense-item-quantity"
              type="number"
              min="1"
              step="1"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              required
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-unit-amount">
              {translations.fields.unitAmountTtc}
            </label>

            <input
              id="expense-item-unit-amount"
              type="number"
              min="0"
              step="0.01"
              value={unitAmountTtc}
              onChange={(event) => setUnitAmountTtc(event.target.value)}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-total">
              {translations.fields.totalAmountTtc}
            </label>

            <input
              id="expense-item-total"
              type="number"
              min="0"
              step="0.01"
              value={amountTtc}
              onChange={(event) => setAmountTtc(event.target.value)}
              required
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-vat-rate">
              {translations.fields.vatRate}
            </label>

            <input
              id="expense-item-vat-rate"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={vatRate}
              onChange={(event) => setVatRate(event.target.value)}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-vat-amount">
              {translations.fields.vatAmount}
            </label>

            <input
              id="expense-item-vat-amount"
              type="number"
              min="0"
              step="0.01"
              value={vatAmount}
              onChange={(event) => setVatAmount(event.target.value)}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-amount-ht">
              {translations.fields.amountHt}
            </label>

            <input
              id="expense-item-amount-ht"
              type="number"
              min="0"
              step="0.01"
              value={amountHt}
              onChange={(event) => setAmountHt(event.target.value)}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-item-vat-recoverable">
              {translations.fields.vatRecoverable}
            </label>

            <select
              id="expense-item-vat-recoverable"
              value={isVatRecoverable ? "true" : "false"}
              onChange={(event) =>
                setIsVatRecoverable(event.target.value === "true")
              }
            >
              <option value="true">{translations.yes}</option>
              <option value="false">{translations.no}</option>
            </select>
          </div>

          <div
            className="
              edit-expense-form-group
              edit-expense-form-group-full
            "
          >
            <label htmlFor="expense-item-comment">
              {translations.fields.comment}
            </label>

            <textarea
              id="expense-item-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={4}
            />
          </div>
        </div>

        <div className="edit-expense-form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            {translations.cancel}
          </button>

          <button type="submit" className="primary-btn" disabled={loading}>
            {loading ? translations.saving : translations.save}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditExpenseReportItemForm;
